import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { adminGetStats, adminSeed } from '../../api/client';
import { Briefcase, Code2, Award, MessageSquare, RefreshCw, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const holo = {
  background: 'linear-gradient(135deg, #00e5ff, #9b00ff, #ff0099, #ffb700, #00e5ff)',
  backgroundSize: '400% 400%',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  animation: 'holo-shift 5s ease infinite',
};

function StatCard({ icon, label, value, color, sub }) {
  return (
    <div style={{
      background: 'var(--card)',
      border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: 4, padding: '22px 24px',
      position: 'relative', overflow: 'hidden',
      transition: 'transform 0.2s, border-color 0.2s',
    }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.borderColor = `${color}35`; }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: `linear-gradient(90deg, transparent, ${color}, transparent)`, opacity: 0.5 }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: '#6a6a88', letterSpacing: 2, marginBottom: 10 }}>{label.toUpperCase()}</div>
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 36, color: '#f2f2ff', lineHeight: 1, marginBottom: 6 }}>{value}</div>
          {sub && <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: '#6a6a88' }}>{sub}</div>}
        </div>
        <div style={{ width: 40, height: 40, borderRadius: 3, background: `${color}10`, border: `1px solid ${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>{icon}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ projects: 0, skills: 0, certifications: 0, messages: 0, unread: 0 });
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const loadStats = async () => {
    try { setStats(await adminGetStats()); } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { loadStats(); }, []);

  const handleSeed = async () => {
    setSeeding(true);
    try { const r = await adminSeed(); toast.success(r.message); loadStats(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSeeding(false); }
  };

  const quickLinks = [
    { label: 'EDIT PROFIL', href: '/admin/profile', color: '#00e5ff' },
    { label: 'TAMBAH PROYEK', href: '/admin/portfolio', color: '#9b00ff' },
    { label: 'LIHAT PESAN', href: '/admin/messages', color: '#ff0099' },
    { label: 'PENGATURAN', href: '/admin/site', color: '#ffb700' },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: '#6a6a88', letterSpacing: 3, marginBottom: 8 }}>OVERVIEW</div>
        <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 28, color: '#f2f2ff', margin: '0 0 6px', letterSpacing: '-0.5px' }}>Dashboard</h1>
        <p style={{ fontSize: 13, color: '#6a6a88', margin: 0 }}>Selamat datang, <span style={{ color: '#f2f2ff' }}>{user?.email}</span></p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 }} className='stats-grid'>
        <StatCard icon={<Briefcase size={18} />} label='Proyek' value={loading ? '—' : stats.projects} color='#00e5ff' sub='Portfolio aktif' />
        <StatCard icon={<Code2 size={18} />} label='Keahlian' value={loading ? '—' : stats.skills} color='#9b00ff' sub='Semua kategori' />
        <StatCard icon={<Award size={18} />} label='Sertifikasi' value={loading ? '—' : stats.certifications} color='#ffb700' sub='Terdaftar' />
        <StatCard icon={<MessageSquare size={18} />} label='Pesan' value={loading ? '—' : stats.messages} color='#ff0099' sub={`${stats.unread} belum dibaca`} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Quick links */}
        <div style={{ background: 'var(--card)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4, padding: 24 }}>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: '#6a6a88', letterSpacing: 2, marginBottom: 16 }}>AKSI CEPAT</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {quickLinks.map(l => (
              <a key={l.label} href={l.href}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 14px', borderRadius: 3,
                  background: `${l.color}06`,
                  border: `1px solid ${l.color}20`,
                  color: l.color, textDecoration: 'none',
                  fontFamily: "'DM Mono', monospace",
                  fontWeight: 600, fontSize: 11, letterSpacing: 0.5,
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = `${l.color}12`; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = `${l.color}06`; e.currentTarget.style.transform = 'none'; }}
              >
                {l.label}
                <ArrowRight size={12} />
              </a>
            ))}
          </div>
        </div>

        {/* Seed */}
        <div style={{ background: 'var(--card)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4, padding: 24 }}>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: '#6a6a88', letterSpacing: 2, marginBottom: 12 }}>SETUP AWAL</div>
          <p style={{ fontSize: 13, color: '#6a6a88', lineHeight: 1.7, marginBottom: 20, margin: '0 0 20px' }}>
            Pertama kali pakai? Isi data awal ke database MySQL secara otomatis.
          </p>
          <button onClick={handleSeed} disabled={seeding}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 20px', borderRadius: 3, border: 'none',
              background: seeding ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg, #00e5ff, #9b00ff)',
              color: seeding ? '#6a6a88' : '#07070f',
              fontFamily: "'DM Mono', monospace",
              fontWeight: 600, fontSize: 11, letterSpacing: 1,
              cursor: seeding ? 'not-allowed' : 'pointer',
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={e => { if (!seeding) e.currentTarget.style.opacity = '0.85'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
          >
            <RefreshCw size={13} style={{ animation: seeding ? 'spin 1s linear infinite' : 'none' }} />
            {seeding ? 'MEMPROSES...' : 'SEED DATA AWAL'}
          </button>
        </div>
      </div>
    </div>
  );
}
